import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useManagementStore } from "../../../store/managementStore";
import {
	CalendarIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";

const ACTION_OPTIONS = [
	{ value: "", label: "전체" },
	{ value: "auth.login", label: "로그인" },
	{ value: "auth.logout", label: "로그아웃" },
	{ value: "login", label: "로그인(레거시)" },
	{ value: "logout", label: "로그아웃(레거시)" },
	{ value: "tickets.create", label: "티켓 등록" },
	{ value: "tickets.update", label: "티켓 수정" },
	{ value: "tickets.delete", label: "티켓 삭제" },
	{ value: "seats.assign", label: "좌석 배정" },
	{ value: "seats.cancel", label: "좌석 배정 취소" },
	{ value: "seatmap.layout.create", label: "좌석배치도 레이아웃 생성" },
	{ value: "seatmap.layout.update", label: "좌석배치도 레이아웃 수정" },
	{ value: "seatmap.layout.delete", label: "좌석배치도 레이아웃 삭제" },
	{ value: "seatmap.train_type.create", label: "좌석배치도 열차유형 생성" },
	{ value: "seatmap.train_type.update", label: "좌석배치도 열차유형 수정" },
	{ value: "create_user", label: "사용자 생성" },
	{ value: "update_user", label: "사용자 수정" },
	{ value: "delete_user", label: "사용자 삭제" },
	{ value: "create_role", label: "역할 생성" },
	{ value: "update_role", label: "역할 수정" },
	{ value: "delete_role", label: "역할 삭제" },
];

const RESOURCE_OPTIONS = [
	{ value: "", label: "전체" },
	{ value: "auth", label: "인증" },
	{ value: "ticket", label: "티켓" },
	{ value: "seat", label: "좌석" },
	{ value: "seatmap", label: "좌석배치도" },
	{ value: "user", label: "사용자" },
	{ value: "role", label: "역할" },
];

const SUCCESS_OPTIONS = [
	{ value: "", label: "전체" },
	{ value: "true", label: "성공" },
	{ value: "false", label: "실패" },
];

const ACTION_LABELS = {
	"auth.login": "로그인",
	"auth.logout": "로그아웃",
	login: "로그인(레거시)",
	logout: "로그아웃(레거시)",
	"tickets.create": "티켓 등록",
	"tickets.update": "티켓 수정",
	"tickets.delete": "티켓 삭제",
	"seats.assign": "좌석 배정",
	"seats.cancel": "좌석 배정 취소",
	"seatmap.layout.create": "좌석배치도 레이아웃 생성",
	"seatmap.layout.update": "좌석배치도 레이아웃 수정",
	"seatmap.layout.delete": "좌석배치도 레이아웃 삭제",
	"seatmap.train_type.create": "좌석배치도 열차유형 생성",
	"seatmap.train_type.update": "좌석배치도 열차유형 수정",
	create_user: "사용자 생성",
	update_user: "사용자 수정",
	delete_user: "사용자 삭제",
	create_role: "역할 생성",
	update_role: "역할 수정",
	delete_role: "역할 삭제",
};

const RESOURCE_LABELS = {
	auth: "인증",
	ticket: "티켓",
	seat: "좌석",
	seatmap: "좌석배치도",
	user: "사용자",
	role: "역할",
};

const getActionLabel = (action) => {
	if (!action) return "-";
	return ACTION_LABELS[action] || action;
};

const getResourceLabel = (resource) => {
	if (!resource) return "-";
	return RESOURCE_LABELS[resource] || resource;
};

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	return date.toLocaleString("ko-KR", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const parseDetails = (details) => {
	if (!details) return null;
	if (typeof details === "object") return details;
	try {
		return JSON.parse(details);
	} catch (err) {
		return details;
	}
};

const buildAuditLogParams = (filters, page, limit) => {
	const next = {
		limit,
		offset: page * limit,
	};
	if (filters.startDate) next.start_date = filters.startDate;
	if (filters.endDate) next.end_date = filters.endDate;
	if (filters.action) next.action = filters.action;
	if (filters.resource) next.resource = filters.resource;
	if (filters.success) next.success = filters.success === "true";
	if (filters.userId) next.user_id = Number(filters.userId);
	if (filters.reservationNumber) next.reservation_number = filters.reservationNumber.trim();
	if (filters.eventId) next.event_id = filters.eventId.trim();
	return next;
};

const AuditLogDetailModal = ({ log, onClose }) => {
	if (!log) return null;
	const parsed = parseDetails(log.details);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
			onClick={(event) => {
				if (event.target === event.currentTarget) {
					onClose();
				}
			}}
		>
			<div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] overflow-auto">
				<div className="flex items-center justify-between px-6 py-4 border-b">
					<div>
						<h2 className="text-lg font-semibold">감사 로그 상세</h2>
						<p className="text-sm text-gray-500">
							{getActionLabel(log.action)} · {formatDateTime(log.created_at)}
						</p>
					</div>
					<button
						className="p-1 rounded hover:bg-gray-100"
						onClick={onClose}
					>
						<XMarkIcon className="w-5 h-5" />
					</button>
				</div>
				<div className="p-6 space-y-4">
					<div className="grid grid-cols-2 gap-4 text-sm">
						<div>
							<p className="text-gray-500">사용자</p>
							<p className="font-medium">
								{log.username || log.user_id || "-"}
							</p>
						</div>
						<div>
							<p className="text-gray-500">리소스</p>
							<p className="font-medium">
								{getResourceLabel(log.resource)}
								{log.resource_id && log.resource ? ` / ${log.resource_id}` : ""}
							</p>
						</div>
						<div>
							<p className="text-gray-500">성공 여부</p>
							<p className="font-medium">{log.success === false ? "실패" : "성공"}</p>
						</div>
						<div>
							<p className="text-gray-500">IP</p>
							<p className="font-medium">{log.ip_address || "-"}</p>
						</div>
					</div>
					<div>
						<p className="text-gray-500 text-sm mb-2">상세 내용</p>
						<pre className="bg-gray-50 border rounded p-4 text-xs whitespace-pre-wrap">
							{parsed ? JSON.stringify(parsed, null, 2) : "-"}
						</pre>
					</div>
				</div>
			</div>
		</div>
	);
};

export default function ManagementLog() {
	const { getAuditLogs, isLoading } = useManagementStore();
	const [logs, setLogs] = useState([]);
	const [total, setTotal] = useState(0);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [action, setAction] = useState("");
	const [resource, setResource] = useState("");
	const [success, setSuccess] = useState("");
	const [userId, setUserId] = useState("");
	const [reservationNumber, setReservationNumber] = useState("");
	const [eventId, setEventId] = useState("");
	const [page, setPage] = useState(0);
	const [appliedFilters, setAppliedFilters] = useState(null);
	const [selectedLog, setSelectedLog] = useState(null);
	const limit = 50;

	useEffect(() => {
		const today = new Date().toISOString().slice(0, 10);
		setStartDate(today);
		setEndDate(today);
		setAppliedFilters({
			startDate: today,
			endDate: today,
			action: "",
			resource: "",
			success: "",
			userId: "",
			reservationNumber: "",
			eventId: "",
		});
	}, []);

	const params = useMemo(() => {
		if (!appliedFilters) return null;
		return buildAuditLogParams(appliedFilters, page, limit);
	}, [appliedFilters, page, limit]);

	const fetchLogs = useCallback(async (requestParams) => {
		const response = await getAuditLogs(requestParams);
		if (response?.success) {
			setLogs(response.data);
			setTotal(response.total || 0);
		}
	}, [getAuditLogs]);

	useEffect(() => {
		if (params) {
			fetchLogs(params);
		}
	}, [fetchLogs, params]);

	const handleSearch = () => {
		setAppliedFilters({
			startDate,
			endDate,
			action,
			resource,
			success,
			userId,
			reservationNumber,
			eventId,
		});
		setPage(0);
	};

	const totalPages = Math.ceil(total / limit) || 1;

	return (
		<div className="py-6">
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold text-gray-900">로그 조회</h1>
				<span className="text-sm text-gray-500">총 {total.toLocaleString()}건</span>
			</div>

			<div className="bg-white border rounded-lg p-4 mb-6">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							시작일
						</label>
						<div className="relative">
							<CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
							<input
								type="date"
								value={startDate}
								onChange={(event) => setStartDate(event.target.value)}
								className="pl-10 pr-3 py-2 border rounded w-full"
							/>
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							종료일
						</label>
						<div className="relative">
							<CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
							<input
								type="date"
								value={endDate}
								onChange={(event) => setEndDate(event.target.value)}
								className="pl-10 pr-3 py-2 border rounded w-full"
							/>
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							사용자 ID
						</label>
						<input
							type="number"
							value={userId}
							onChange={(event) => setUserId(event.target.value)}
							className="px-3 py-2 border rounded w-full"
							placeholder="예: 1"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							예약 번호
						</label>
						<input
							type="text"
							value={reservationNumber}
							onChange={(event) => setReservationNumber(event.target.value)}
							className="px-3 py-2 border rounded w-full"
							placeholder="예: 28520-1229-10061-87"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							행사 ID
						</label>
						<input
							type="text"
							value={eventId}
							onChange={(event) => setEventId(event.target.value)}
							className="px-3 py-2 border rounded w-full"
							placeholder="예: 202601-222"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							액션
						</label>
						<select
							value={action}
							onChange={(event) => setAction(event.target.value)}
							className="px-3 py-2 border rounded w-full"
						>
							{ACTION_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							리소스
						</label>
						<select
							value={resource}
							onChange={(event) => setResource(event.target.value)}
							className="px-3 py-2 border rounded w-full"
						>
							{RESOURCE_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							성공 여부
						</label>
						<select
							value={success}
							onChange={(event) => setSuccess(event.target.value)}
							className="px-3 py-2 border rounded w-full"
						>
							{SUCCESS_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="flex justify-end mt-4">
					<button
						onClick={handleSearch}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition duration-150 ease-in-out disabled:opacity-50"
						disabled={isLoading}
					>
						<div className="flex items-center">
							<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
							조회하기
						</div>
					</button>
				</div>
			</div>

			<div className="bg-white border rounded-lg overflow-hidden">
				{isLoading ? (
					<div className="p-8 text-center text-gray-500">로딩 중...</div>
				) : logs.length === 0 ? (
					<div className="p-8 text-center text-gray-500">
						조회된 로그가 없습니다.
					</div>
				) : (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용자</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">액션</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">리소스</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상세</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{logs.map((log) => (
								<tr key={log.id}>
									<td className="px-4 py-3 text-sm text-gray-500">
										{formatDateTime(log.created_at)}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{log.username || log.user_id || "-"}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{getActionLabel(log.action)}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{getResourceLabel(log.resource)}
										{log.resource_id && log.resource ? ` / ${log.resource_id}` : ""}
									</td>
									<td className="px-4 py-3 text-sm">
										<span
											className={`px-2 py-1 rounded text-xs ${
												log.success === false
													? "bg-red-100 text-red-700"
													: "bg-green-100 text-green-700"
											}`}
										>
											{log.success === false ? "실패" : "성공"}
										</span>
									</td>
									<td className="px-4 py-3 text-sm text-gray-500">{log.ip_address || "-"}</td>
									<td className="px-4 py-3 text-sm">
										<button
											className="text-blue-600 hover:text-blue-800"
											onClick={() => setSelectedLog(log)}
										>
											보기
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			<div className="flex items-center justify-between mt-4">
				<button
					className="flex items-center gap-1 px-3 py-2 text-sm border rounded disabled:opacity-50"
					onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
					disabled={page === 0}
				>
					<ChevronLeftIcon className="w-4 h-4" />
					이전
				</button>
				<span className="text-sm text-gray-600">
					{page + 1} / {totalPages}
				</span>
				<button
					className="flex items-center gap-1 px-3 py-2 text-sm border rounded disabled:opacity-50"
					onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
					disabled={page >= totalPages - 1}
				>
					다음
					<ChevronRightIcon className="w-4 h-4" />
				</button>
			</div>

			{selectedLog && (
				<AuditLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
			)}
		</div>
	);
}
